import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Card,
    CardContent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    TablePagination,
    TextField,
    Button,
    Tooltip,
    Typography,
    CircularProgress,
    FormControl,
    Select,
    MenuItem,
    InputAdornment,
    Chip,
    Badge,
    Alert,
    IconButton,
    Paper,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Stack,
} from '@mui/material';
import {
    Search as SearchIcon,
    Add as AddIcon,
    FilterList as FilterIcon,
    CalendarToday as CalendarIcon,
    PriorityHigh as PriorityHighIcon,
    Refresh as RefreshIcon,
    Clear as ClearIcon,
    CheckCircle as CheckCircleIcon,
    Pending as PendingIcon,
    Schedule as ScheduleIcon,
} from '@mui/icons-material';
import TodayIcon from '@mui/icons-material/Today';
import FilterListAltIcon from '@mui/icons-material/FilterListAlt';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, startOfDay, isAfter, differenceInDays } from 'date-fns';
import Brandcrumb from '../../../../componentsUiBlock/Brandcrumb';
import DeleteConfirmDialog from '../../../../componentsUiBlock/DeleteConfirmDialog';
import { request } from '../../../../utils/request';
import { useDebounce } from '../../../../utils/useDebounce';
import { toast } from 'react-toastify';
import { Priority, Reminder, Status } from '../../types';

// Helper functions
const getPriorityColor = (priority?: Priority) => {
    switch (priority) {
        case 'HIGH': return 'error';
        case 'MEDIUM': return 'warning';
        case 'LOW': return 'success';
        default: return 'default';
    }
};

const getPriorityIcon = (priority?: Priority) => {
    switch (priority) {
        case 'HIGH': return <PriorityHighIcon color="error" fontSize="small" />;
        case 'MEDIUM': return <PriorityHighIcon color="warning" fontSize="small" />;
        case 'LOW': return <PriorityHighIcon color="success" fontSize="small" />;
        default: return <PriorityHighIcon color="disabled" fontSize="small" />;
    }
};

const getStatusColor = (status?: Status) => {
    switch (status) {
        case 'PENDING': return 'warning';
        case 'CLOSED': return 'success';
        default: return 'default';
    }
};

const getStatusIcon = (status?: Status) => {
    switch (status) {
        case 'PENDING': return <PendingIcon fontSize="small" />;
        case 'CLOSED': return <CheckCircleIcon fontSize="small" />;
        default: return <ScheduleIcon fontSize="small" />;
    }
};

const getStatusLabel = (status?: Status) => {
    switch (status) {
        case 'PENDING': return 'Pending';
        case 'CLOSED': return 'Closed';
        default: return 'Unknown';
    }
};

const formatDate = (date: any) => {
    if (!date) return '';
    return format(new Date(date), 'dd/MM/yyyy HH:mm');
};

const formatDateOnly = (date: any) => {
    if (!date) return '';
    return format(new Date(date), 'dd/MM/yyyy');
};

const isOverdue = (reminderDate: Date | string) => {
    return isAfter(new Date(), new Date(reminderDate));
};

const getReminderStatus = (reminder: Reminder) => {
    if (reminder?.status === 'CLOSED') return 'closed';
    if (isOverdue(reminder.reminderDate)) return 'overdue';
    const daysLeft = differenceInDays(new Date(reminder.reminderDate), new Date());
    if (daysLeft <= 1) return 'urgent';
    return 'upcoming';
};

const ListReminder: React.FC = () => {
    const navigate = useNavigate();

    // State for reminders
    const [reminders, setReminders] = useState<Reminder[]>([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);

    // Filter states
    const [search, setSearch] = useState('');
    const debouncedSearch = useDebounce(search, 500);
    const [statusFilter, setStatusFilter] = useState<string>('ALL');
    const [priorityFilter, setPriorityFilter] = useState<string>('ALL');
    const [dateRange, setDateRange] = useState<{ start: Date | null; end: Date | null }>({
        start: null,
        end: null,
    });
    const [quickDateFilter, setQuickDateFilter] = useState<string>('ALL');

    // Sorting and pagination
    const [order, setOrder] = useState<'asc' | 'desc'>('desc');
    const [orderBy, setOrderBy] = useState<string>('reminderDate');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [total, setTotal] = useState(0);

    // Delete dialog
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [itemToDelete, setItemToDelete] = useState<Reminder | null>(null);
    const [loadingDelete, setLoadingDelete] = useState(false);

    // Build query params from filters
    const buildQueryParams = useCallback(() => {
        const params: any = {
            page: page + 1,
            limit: rowsPerPage,
            sortBy: orderBy,
            sortOrder: order,
        };

        if (debouncedSearch) params.search = debouncedSearch;
        if (statusFilter !== 'ALL') params.status = statusFilter;
        if (priorityFilter !== 'ALL') params.priority = priorityFilter;

        // Handle date filters
        if (dateRange.start || dateRange.end) {
            if (dateRange.start) params.startDate = format(dateRange.start, 'yyyy-MM-dd');
            if (dateRange.end) params.endDate = format(dateRange.end, 'yyyy-MM-dd');
        } else if (quickDateFilter !== 'ALL') {
            const today = new Date();
            let startDate: Date | null = null;

            switch (quickDateFilter) {
                case 'TODAY':
                    startDate = startOfDay(today);
                    break;
                case 'TOMORROW':
                    startDate = startOfDay(new Date(today.setDate(today.getDate() + 1)));
                    break;
                case 'THIS_WEEK':
                    startDate = startOfDay(new Date(today.setDate(today.getDate() - today.getDay())));
                    break;
                case 'OVERDUE':
                    params.overdue = true;
                    break;
                default:
                    startDate = startOfDay(today);
            }
            if (startDate) params.startDate = format(startDate, 'yyyy-MM-dd');
            if (quickDateFilter !== 'OVERDUE') params.endDate = format(today, 'yyyy-MM-dd');
        }

        return params;
    }, [debouncedSearch, statusFilter, priorityFilter, dateRange, quickDateFilter, page, rowsPerPage, orderBy, order]);

    // Fetch reminders
    const fetchReminders = useCallback(async () => {
        setLoading(true);
        try {
            const params = buildQueryParams();
            const queryString = new URLSearchParams(params).toString();

            const response = await request({
                url: `reminder/get-all-reminders?${queryString}`,
                method: 'get',
            });

            // Note: The response structure should match what your backend returns
            if (response.success) {
                setReminders(response.data || []);
                setTotal(response.total || 0);
            } else {
                toast.error(response.message || 'Failed to load reminders');
            }
        } catch (error: any) {
            console.error('Error fetching reminders:', error);
            toast.error(error?.message || 'Failed to load reminders');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, [buildQueryParams]);

    // Fetch reminders when filters change
    useEffect(() => {
        fetchReminders();
    }, [fetchReminders]);

    // Clear filters
    const handleClearFilters = () => {
        setSearch('');
        setStatusFilter('ALL');
        setPriorityFilter('ALL');
        setDateRange({ start: null, end: null });
        setQuickDateFilter('ALL');
        setPage(0);
    };

    // Sort handler
    const handleSort = (property: string) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    // Pagination handlers
    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Refresh
    const handleRefresh = () => {
        setRefreshing(true);
        fetchReminders();
    };

    // Delete handlers
    const handleDeleteClick = (reminder: Reminder) => {
        setItemToDelete(reminder);
        setOpenDeleteDialog(true);
    };

    const handleDeleteConfirm = async () => {
        if (!itemToDelete) return;

        setLoadingDelete(true);
        try {
            const res = await request({
                url: `reminder/delete-reminder/${itemToDelete._id}`,
                method: 'delete',
            });

            if (res.success) {
                toast.success(res.message || 'Reminder deleted successfully');
                await fetchReminders();
                setOpenDeleteDialog(false);
                setItemToDelete(null);
            }
        } catch (error: any) {
            console.error('Error deleting reminder:', error);
            toast.error(error || 'Failed to delete reminder');
        } finally {
            setLoadingDelete(false);
        }
    };

    const handleDeleteCancel = () => {
        setOpenDeleteDialog(false);
        setItemToDelete(null);
    };

    // Head cells
    const headCells = [
        { id: 'priority', label: 'Priority', sortable: true, width: 100 },
        { id: 'description', label: 'Description', sortable: false, width: 250 },
        { id: 'reminderDate', label: 'Reminder Date', sortable: true, width: 150 },
        { id: 'date', label: 'Event Date', sortable: true, width: 150 },
        { id: 'status', label: 'Status', sortable: true, width: 120 },
        { id: 'nextReminderDate', label: 'Next Reminder', sortable: true, width: 150 },
        { id: 'actions', label: 'Actions', sortable: false, width: 120 },
    ];

    // Check if any filters are active
    const hasActiveFilters = Boolean(
        search ||
        statusFilter !== 'ALL' ||
        priorityFilter !== 'ALL' ||
        dateRange.start ||
        dateRange.end ||
        quickDateFilter !== 'ALL'
    );

    return (
        <>
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'start', alignItems: 'center' }}>
                <Brandcrumb />
            </Box>

            <LocalizationProvider dateAdapter={AdapterDateFns}>
                <Card elevation={4} sx={{ borderRadius: 1 }}>
                    <CardContent sx={{ pb: 2 }}>
                        {/* Header */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={() => navigate("/reminder/createReminder")}
                                startIcon={<AddIcon />}
                                size="small"
                            >
                                New Reminder
                            </Button>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                                <Badge badgeContent={total} showZero color="primary">
                                    <TodayIcon color="action" />
                                </Badge>

                                <TextField
                                    size="small"
                                    placeholder="Search reminders..."
                                    variant="outlined"
                                    sx={{ width: 180 }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon fontSize="small" color="action" />
                                            </InputAdornment>
                                        ),
                                    }}
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    disabled={loading}
                                />

                                <Tooltip title="Refresh" placement='top' arrow>
                                    <IconButton size="small" onClick={handleRefresh} disabled={refreshing || loading}>
                                        {refreshing ? <CircularProgress size={20} /> : <RefreshIcon fontSize="small" />}
                                    </IconButton>
                                </Tooltip>

                                {hasActiveFilters && (
                                    <Tooltip title="Clear Filters" placement='top' arrow>
                                        <IconButton size="small" onClick={handleClearFilters}>
                                            <ClearIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Box>
                        </Box>

                        {/* Filters Accordion */}
                        <Accordion sx={{ my: 2, boxShadow: 'none', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                            <AccordionSummary
                                expandIcon={<FilterListAltIcon />}
                                sx={{ '&:hover': { backgroundColor: '#e9eef6' } }}
                            >
                                Filters
                            </AccordionSummary>
                            <AccordionDetails>
                                <Paper elevation={0} sx={{
                                    p: 1.5,
                                    backgroundColor: 'background.default',
                                    borderRadius: 1,
                                    border: '1px solid',
                                    borderColor: 'divider'
                                }}>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                                        {/* Status Filter */}
                                        <FormControl size="small" sx={{ minWidth: '24%' }}>
                                            <Select
                                                value={statusFilter}
                                                onChange={(e) => setStatusFilter(e.target.value)}
                                                displayEmpty
                                                startAdornment={
                                                    <InputAdornment position="start">
                                                        <FilterIcon fontSize="small" color="action" />
                                                    </InputAdornment>
                                                }
                                                disabled={loading}
                                            >
                                                <MenuItem value="ALL">All Status</MenuItem>
                                                <MenuItem value="PENDING">⏳ Pending</MenuItem>
                                                <MenuItem value="CLOSED">✅ Closed</MenuItem>
                                            </Select>
                                        </FormControl>

                                        {/* Priority Filter */}
                                        <FormControl size="small" sx={{ minWidth: '24%' }}>
                                            <Select
                                                value={priorityFilter}
                                                onChange={(e) => setPriorityFilter(e.target.value)}
                                                displayEmpty
                                                disabled={loading}
                                            >
                                                <MenuItem value="ALL">All Priority</MenuItem>
                                                <MenuItem value="HIGH"><PriorityHighIcon color="error" /> High</MenuItem>
                                                <MenuItem value="MEDIUM"><PriorityHighIcon color="warning" /> Medium</MenuItem>
                                                <MenuItem value="LOW"><PriorityHighIcon color="success" /> Low</MenuItem>
                                            </Select>
                                        </FormControl>

                                        {/* Quick Date Filters */}
                                        <FormControl size="small" sx={{ minWidth: '24%' }}>
                                            <Select
                                                value={quickDateFilter}
                                                onChange={(e) => setQuickDateFilter(e.target.value)}
                                                displayEmpty
                                                startAdornment={
                                                    <InputAdornment position="start">
                                                        <CalendarIcon fontSize="small" color="action" />
                                                    </InputAdornment>
                                                }
                                                disabled={loading}
                                            >
                                                <MenuItem value="ALL">All Dates</MenuItem>
                                                <MenuItem value="TODAY">Today</MenuItem>
                                                <MenuItem value="TOMORROW">Tomorrow</MenuItem>
                                                <MenuItem value="THIS_WEEK">This Week</MenuItem>
                                                <MenuItem value="OVERDUE">Overdue</MenuItem>
                                            </Select>
                                        </FormControl>

                                        {/* Date Range Pickers */}
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '48.7%' }}>
                                            <DatePicker
                                                label="From"
                                                value={dateRange.start}
                                                onChange={(date) => {
                                                    setDateRange(prev => ({ ...prev, start: date ? new Date(date as any) : null }));
                                                    setQuickDateFilter('ALL');
                                                }}
                                                disabled={loading}
                                                slotProps={{
                                                    textField: {
                                                        size: 'small',
                                                        sx: { width: '50%' }
                                                    }
                                                }}
                                            />
                                            <Typography variant="body2">to</Typography>
                                            <DatePicker
                                                label="To"
                                                value={dateRange.end}
                                                onChange={(date) => {
                                                    setDateRange(prev => ({ ...prev, end: date ? new Date(date as any) : null }));
                                                    setQuickDateFilter('ALL');
                                                }}
                                                disabled={loading}
                                                slotProps={{
                                                    textField: {
                                                        size: 'small',
                                                        sx: { width: '50%' }
                                                    }
                                                }}
                                            />
                                        </Box>
                                    </Box>
                                </Paper>
                            </AccordionDetails>
                        </Accordion>

                        {/* Table */}
                        <TableContainer sx={{ maxHeight: 500, overflowX: 'auto' }}>
                            <Table stickyHeader size="small" sx={{  }}>
                                <TableHead>
                                    <TableRow>
                                        {headCells.map((head) => (
                                            <TableCell
                                                key={head.id}
                                                sx={{
                                                    fontWeight: 'bold',
                                                    whiteSpace: 'nowrap',
                                                    width: head.width,
                                                    py: 1
                                                }}
                                            >
                                                {head.sortable ? (
                                                    <TableSortLabel
                                                        active={orderBy === head.id}
                                                        direction={orderBy === head.id ? order : 'asc'}
                                                        onClick={() => handleSort(head.id)}
                                                        sx={{ fontSize: '0.8rem' }}
                                                        disabled={loading}
                                                    >
                                                        {head.label}
                                                    </TableSortLabel>
                                                ) : (
                                                    <Typography variant="caption" fontWeight="bold">
                                                        {head.label}
                                                    </Typography>
                                                )}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {loading ? (
                                        <TableRow>
                                            <TableCell colSpan={headCells.length} align="center" sx={{ py: 3 }}>
                                                <CircularProgress size={24} />
                                                <Typography variant="body2" sx={{ mt: 1 }}>
                                                    Loading reminders...
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : reminders.length > 0 ? (
                                        reminders.map((reminder) => {
                                            const reminderStatus = getReminderStatus(reminder);
                                            const isOverdueReminder = reminderStatus === 'overdue';
                                            const isUrgent = reminderStatus === 'urgent';

                                            return (
                                                <TableRow
                                                    key={reminder._id}
                                                    hover

                                                >
                                                    <TableCell sx={{ whiteSpace: 'nowrap', py: 1 }}>
                                                        <Chip
                                                            icon={getPriorityIcon(reminder.priority)}
                                                            label={reminder.priority || 'MEDIUM'}
                                                            size="small"
                                                            color={getPriorityColor(reminder.priority)}
                                                            variant="outlined"
                                                            sx={{ fontSize: '0.7rem', height: 24 }}
                                                        />
                                                    </TableCell>

                                                    <TableCell sx={{
                                                        maxWidth: 250,
                                                        whiteSpace: 'nowrap',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        py: 1
                                                    }}>
                                                        <Tooltip title={reminder.description || 'No description'} arrow>
                                                            <Typography variant="body2" noWrap fontSize="0.8rem">
                                                                {reminder.description || 'No description'}
                                                            </Typography>
                                                        </Tooltip>
                                                    </TableCell>

                                                    <TableCell sx={{ whiteSpace: 'nowrap', py: 1 }}>
                                                        <Box display="flex" alignItems="center" gap={0.5} sx={{ whiteSpace: 'nowrap' }}>
                                                            <ScheduleIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                                            <Typography
                                                                variant="body2"
                                                                fontSize="0.8rem"
                                                                fontWeight={isOverdueReminder ? 'bold' : 'normal'}
                                                                color={isOverdueReminder ? 'error.main' : isUrgent ? 'warning.main' : 'text.primary'}
                                                                noWrap
                                                            >
                                                                {formatDate(reminder.reminderDate)}
                                                            </Typography>
                                                        </Box>
                                                    </TableCell>

                                                    <TableCell sx={{ whiteSpace: 'nowrap', py: 1 }}>
                                                        <Typography variant="body2" fontSize="0.8rem" noWrap>
                                                            {formatDateOnly(reminder.date)}
                                                        </Typography>
                                                    </TableCell>

                                                    <TableCell sx={{ whiteSpace: 'nowrap', py: 1 }}>
                                                        <Chip
                                                            icon={getStatusIcon(reminder?.status)}
                                                            label={getStatusLabel(reminder?.status)}
                                                            size="small"
                                                            color={getStatusColor(reminder?.status)}
                                                            sx={{ fontSize: '0.7rem', height: 24 }}
                                                        />
                                                    </TableCell>

                                                    <TableCell sx={{ whiteSpace: 'nowrap', py: 1 }}>
                                                        {reminder.nextReminderDate ? (
                                                            <Typography variant="body2" fontSize="0.8rem" noWrap>
                                                                {formatDate(reminder.nextReminderDate)}
                                                            </Typography>
                                                        ) : (
                                                            <Typography variant="caption" color="text.secondary" noWrap>
                                                                No next reminder
                                                            </Typography>
                                                        )}
                                                    </TableCell>

                                                    <TableCell sx={{ whiteSpace: 'nowrap', py: 1 }}>
                                                        <Stack direction="row" spacing={1} sx={{ whiteSpace: 'nowrap' }}>
                                                            <Button
                                                                variant="text"
                                                                onClick={() => navigate(`/reminder/${reminder._id}`)}
                                                                sx={{
                                                                    minWidth: 'auto',
                                                                    fontSize: '0.60rem',
                                                                    py: 0.1,
                                                                    px: 2,
                                                                    borderRadius: 5,
                                                                    border: '.8px solid #7f5ee666',
                                                                    whiteSpace: 'nowrap',
                                                                    '&:hover': { backgroundColor: '#7f5ee666' }
                                                                }}
                                                            >
                                                                View / Edit
                                                            </Button>
                                                            <Button
                                                                variant="text"
                                                                color="error"
                                                                onClick={() => handleDeleteClick(reminder)}
                                                                sx={{
                                                                    minWidth: 'auto',
                                                                    fontSize: '0.60rem',
                                                                    py: 0.1,
                                                                    px: 2,
                                                                    borderRadius: 5,
                                                                    border: '.8px solid #ff4d6e5e',
                                                                    whiteSpace: 'nowrap',
                                                                    '&:hover': { backgroundColor: '#ff4d6e5e' }
                                                                }}
                                                            >
                                                                Delete
                                                            </Button>
                                                        </Stack>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={headCells.length} align="center" sx={{ py: 3 }}>
                                                <Typography variant="body2" color="text.secondary">
                                                    {hasActiveFilters
                                                        ? 'No reminders match your filters'
                                                        : 'No reminders found. Create your first reminder!'}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {/* Pagination */}
                        <TablePagination
                            rowsPerPageOptions={[5, 10, 25, 50]}
                            component="div"
                            count={total}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            sx={{
                                borderTop: '1px solid',
                                borderColor: 'divider',
                                '.MuiTablePagination-toolbar': {
                                    minHeight: 52,
                                    padding: 0,
                                },
                                '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                                    fontSize: '0.8rem',
                                }
                            }}
                        />
                    </CardContent>
                </Card>
            </LocalizationProvider>

            {/* Delete Confirmation Dialog */}
            <DeleteConfirmDialog
                open={openDeleteDialog}
                loading={loadingDelete}
                title="Delete Reminder"
                description={
                    <>
                        Are you sure you want to delete this reminder?
                        <br /><br />
                        <Alert severity="warning" sx={{ fontSize: "12px", py: "0px" }}>
                            This action cannot be undone. All associated data will be permanently removed.
                        </Alert>
                    </>
                }
                onCancel={handleDeleteCancel}
                onConfirm={handleDeleteConfirm}
            />
        </>
    );
};

export default ListReminder;